import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Command, ChevronDown, FileText, Wrench, RefreshCw, Users, Menu, X, LogOut, User, Clock, Timer, Globe } from 'lucide-react';
import { DarkModeToggle } from './DarkModeToggle';
import { useDarkMode } from '../hooks/useDarkMode';
import { useAuth } from '../contexts/AuthContext';
import { useTimeTracking } from '../hooks/useTimeTracking';
import { authService } from '../services/authService';
import { activityTracker } from '../services/activityTracker';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { user, logout } = useAuth();
  const { trackingState, isLoading, error, clockIn, clockOut, getStatusDisplay, canTrackTime } = useTimeTracking();
  const location = useLocation();
  const [isToolsOpen, setIsToolsOpen] = useState(true);
  const [isOrdersOpen, setIsOrdersOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [clockStatus, setClockStatus] = useState<{ isClockedIn: boolean; clockInTime?: string; currentSessionDuration?: string }>({ isClockedIn: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsToolsOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Close mobile menu on route change (desktop dropdowns persist)
    setIsMobileMenuOpen(false);
  }, [location]);

  // Persist dropdown open states across refresh/navigation
  useEffect(() => {
    try {
      const storedOrders = localStorage.getItem('sidebar.ordersOpen');
      const storedTools = localStorage.getItem('sidebar.toolsOpen');
      if (storedOrders !== null) setIsOrdersOpen(storedOrders === 'true');
      if (storedTools !== null) setIsToolsOpen(storedTools === 'true');
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (user?.userType === 'manager') {
      // Initialize activity tracker for managers
      activityTracker.init();
      
      // Check initial clock status
      checkClockStatus();
      
      // Check clock status every 30 seconds
      const interval = setInterval(checkClockStatus, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  const isActive = (path: string) => location.pathname === path;
  const isToolActive = () => location.pathname.startsWith('/tools');
  const isOrdersActive = () => location.pathname === '/' || location.pathname.startsWith('/orders/');

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    
    // Clear activity tracking when logging out
    if (activityTracker.isCurrentlyTracking()) {
      // Activity tracker will handle stopping automatically
    }
    
    window.location.href = '/';
  };

  const handleClockAction = async () => {
    if (trackingState.isClockedIn) {
      await clockOut();
    } else {
      await clockIn();
    }
  };

  const checkClockStatus = async () => {
    if (!user || user.userType !== 'manager') return;

    try {
      const response = await fetch(`/api/managers/${user.username}/clock-status`);
      const result = await response.json();
      
      if (result.success) {
        setClockStatus(result.data);
      }
    } catch (error) {
      console.error('Error checking clock status:', error);
    }
  };

  const handleClockIn = async () => {
    if (!user || user.userType !== 'manager') return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/managers/clock-in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username: user.username })
      });
      
      const result = await response.json();
      if (result.success) {
        await checkClockStatus();
        // Activity tracker will automatically start tracking when clock status changes
      } else {
        alert(result.error || 'Failed to clock in');
      }
    } catch (error) {
      console.error('Error clocking in:', error);
      alert('Failed to clock in');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!user || user.userType !== 'manager') return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/managers/clock-out', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username: user.username })
      });
      
      const result = await response.json();
      if (result.success) {
        await checkClockStatus();
        // Activity tracker will automatically stop tracking when clock status changes
      } else {
        alert(result.error || 'Failed to clock out');
      }
    } catch (error) {
      console.error('Error clocking out:', error);
      alert('Failed to clock out');
    } finally {
      setLoading(false);
    }
  };

  const toolsItems = [
    { path: '/tools/create-zone', label: 'Create Zone' },
    { path: '/tools/check-domain-status', label: 'Check Domain Status' },
    { path: '/tools/nameservers', label: 'Update Nameservers' },
    { path: '/tools/google-reseller', label: 'Google Reseller Helper' },
  ];

  const formatClockTime = (timeString: string) => {
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border border-border"
      >
        {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 z-40
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isDarkMode 
          ? 'bg-black/40 backdrop-blur-2xl border-r border-white/10 shadow-2xl shadow-black/50' 
          : 'bg-card/95 backdrop-blur-xl border-r border-border'
        }
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="Pager.ai Logo" 
                className="h-8 w-8 rounded-lg object-contain"
              />
              <span className="text-lg font-semibold">Pager.ai</span>
            </Link>
          </div>

          {/* Time Tracking Section for Managers */}
          {canTrackTime && (
            <div className="p-4 border-b border-border">
              <div className="space-y-3">
                {/* Clock Status Display */}
                <div className="text-xs text-muted-foreground">
                  {clockStatus.isClockedIn ? (
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                        Clocked In
                      </span>
                      {clockStatus.clockInTime && (
                        <span className="text-xs text-gray-600">
                          since {formatClockTime(clockStatus.clockInTime)}
                        </span>
                      )}
                      {clockStatus.currentSessionDuration && (
                        <span className="text-xs text-gray-600">
                          ({clockStatus.currentSessionDuration})
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Clocked Out
                    </span>
                  )}
                </div>
                
                {/* Clock In/Out Button */}
                <div className="flex space-x-2">
                  {!clockStatus.isClockedIn ? (
                    <button
                      onClick={handleClockIn}
                      disabled={loading}
                      className={`w-full flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        loading ? 'bg-green-500 text-white' : 'bg-green-500 hover:bg-green-600 text-white'
                      } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {loading ? 'Clocking In...' : 'Clock In'}
                    </button>
                  ) : (
                    <button
                      onClick={handleClockOut}
                      disabled={loading}
                      className={`w-full flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        loading ? 'bg-red-500 text-white' : 'bg-red-500 hover:bg-red-600 text-white'
                      } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {loading ? 'Clocking Out...' : 'Clock Out'}
                    </button>
                  )}
                </div>

                {/* Current Session Duration */}
                {trackingState.isClockedIn && (
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Current Session</div>
                    <div className="text-sm font-medium text-primary">
                      {(() => {
                        const hours = Math.floor(trackingState.currentSessionDuration / (1000 * 60 * 60));
                        const minutes = Math.floor((trackingState.currentSessionDuration % (1000 * 60 * 60)) / (1000 * 60));
                        const seconds = Math.floor((trackingState.currentSessionDuration % (1000 * 60)) / 1000);
                        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                      })()}
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <div className="text-xs text-red-500 text-center">
                    {error}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => {
                  const next = !isOrdersOpen;
                  setIsOrdersOpen(next);
                  try { localStorage.setItem('sidebar.ordersOpen', String(next)); } catch (_) {}
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isOrdersActive()
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Users className="h-4 w-4" />
                  <span>Pending Orders</span>
                </div>
                <ChevronDown className={`h-3 w-3 transition-transform ${isOrdersOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOrdersOpen && (
                <div className="mt-1 space-y-1 animate-fade-in">
                  <Link
                    to="/orders/instantly"
                    className={`block pl-11 pr-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/orders/instantly')
                        ? 'bg-muted text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                    }`}
                  >
                    Instantly Orders
                  </Link>
                  <Link
                    to="/orders/smartlead"
                    className={`block pl-11 pr-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/orders/smartlead')
                        ? 'bg-muted text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                    }`}
                  >
                    Smartlead Orders
                  </Link>
                  <Link
                    to="/orders/manual"
                    className={`block pl-11 pr-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/orders/manual')
                        ? 'bg-muted text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                    }`}
                  >
                    Manual Orders
                  </Link>
                </div>
              )}
            </div>
            
            <Link
              to="/admin-reports"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/admin-reports') 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <FileText className="h-4 w-4" />
              <span>Admin Reports</span>
            </Link>
            
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => {
                  const next = !isToolsOpen;
                  setIsToolsOpen(next);
                  try { localStorage.setItem('sidebar.toolsOpen', String(next)); } catch (_) {}
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isToolActive() 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Wrench className="h-4 w-4" />
                  <span>Helper Tools</span>
                </div>
                <ChevronDown className={`h-3 w-3 transition-transform ${isToolsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isToolsOpen && (
                <div className="mt-1 space-y-1 animate-fade-in">
                  {toolsItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`block pl-11 pr-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive(item.path)
                          ? 'bg-muted text-foreground font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link
              to="/reconnects"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/reconnects') 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <RefreshCw className="h-4 w-4" />
              <span>Reconnects</span>
            </Link>

            <Link
              to="/smartlead-cancels"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/smartlead-cancels') 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <X className="h-4 w-4" />
              <span>Smartlead Cancels</span>
            </Link>

            {/* Manage Time Tab */}
            <Link
              to="/manage-time"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/manage-time') 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Timer className="h-4 w-4" />
              <span>Manage Time</span>
            </Link>
          </nav>

          {/* User Menu and Dark Mode Toggle */}
          <div className="p-4 border-t border-border space-y-2">
            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-2 flex-1">
                  <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                    <User className="h-3 w-3 text-primary-foreground" />
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="truncate text-xs">{user?.username || 'User'}</span>
                    {user?.userType === 'manager' && user?.firstName && user?.lastName && (
                      <span className="truncate text-xs text-muted-foreground">
                        {user.firstName} {user.lastName}
                      </span>
                    )}
                    {user?.userType === 'superadmin' && (
                      <span className="truncate text-xs text-orange-500 font-medium">Super Admin</span>
                    )}
                  </div>
                </div>
                <ChevronDown className={`h-3 w-3 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isUserMenuOpen && (
                <div className={`absolute bottom-full left-0 right-0 mb-1 rounded-lg shadow-lg animate-fade-in ${
                  isDarkMode 
                    ? 'bg-black/50 backdrop-blur-xl border border-white/10' 
                    : 'bg-card border border-border'
                }`}>
                  <div className="p-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-sm text-muted-foreground">Theme</span>
              <DarkModeToggle isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-background/80 backdrop-blur-sm z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className={`mx-auto py-12 ${
          location.pathname === '/reconnects' || location.pathname === '/' || location.pathname.startsWith('/users/') || location.pathname.startsWith('/orders/')
            ? 'px-20 max-w-none' 
            : 'container px-4 max-w-6xl'
        }`}>
          {children}
        </div>
      </main>
    </div>
  );
}; 